import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { Menu, MenuButton, MenuItems } from '@headlessui/react';
import { Placement } from '@popperjs/core';
import Icon from 'flow/components/ui-components/Icon';
import {
    RowTabStopContext,
    useRowTabIndex,
} from 'flow/components/ui-components/row-tab-stop';
import { twMerge } from 'tailwind-merge';

export interface LockScroll {
    scroll?: Element;
    wrapper?: Element;
}

type Props = {
    children: React.ReactNode;
    setActive?: (value: boolean) => void;
    className?: string;
    menuClassName?: string;
    dataTestId?: string;
    icon?: React.ReactNode;
    /**
     * Names the trigger button for assistive tech. The button's only content
     * is an icon, and an icon is hidden from screen readers, so without this
     * it announces as an unnamed "button".
     *
     * Deliberately not defaulted here: only the caller knows what the menu is
     * for, and a generic default would be wrong wherever the trigger is not a
     * plain overflow menu. Omit it only when `icon` already carries text of
     * its own, visible or visually hidden.
     */
    triggerLabel?: string;
    lockScroll?: LockScroll;
    preventOverflow?: boolean;
    placement?: Placement;
    setMenuButtonAnchor?: (value: EventTarget) => void;
    skipPortal?: boolean;
    /** The open menu closes when this changes, such as the row's contact. */
    targetKey?: string;
};

interface CloseOnTargetChangeProps {
    targetKey?: string;
    open: boolean;
    close: () => void;
    menuElement: HTMLElement | null;
    triggerElement: HTMLElement | null;
}

const CloseOnTargetChange = ({
    targetKey,
    open,
    close,
    menuElement,
    triggerElement,
}: CloseOnTargetChangeProps): null => {
    const targetKeyRef = useRef(targetKey);

    useLayoutEffect(() => {
        if (targetKeyRef.current === targetKey) {
            return;
        }
        targetKeyRef.current = targetKey;
        if (!open) {
            return;
        }

        const menuHadFocus = menuElement?.contains(document.activeElement);
        close();
        if (menuHadFocus) {
            triggerElement?.focus();
        }
    }, [targetKey, open, close, menuElement, triggerElement]);

    return null;
};

const ContextMenu = ({
    children,
    setActive,
    className,
    menuClassName,
    dataTestId,
    icon,
    triggerLabel,
    lockScroll,
    preventOverflow,
    placement = 'auto-start',
    setMenuButtonAnchor,
    skipPortal = false,
    targetKey,
}: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const rowTabIndex = useRowTabIndex();
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);

    const { styles } = usePopper(referenceElement, popperElement, {
        placement: placement,
        modifiers: [
            {
                name: 'flip',
                enabled: true,
                options: {
                    flipVariations: true,
                    allowedAutoPlacements: [
                        'bottom-start',
                        'top-start',
                        'bottom-end',
                        'top-end',
                    ],
                },
            },
            {
                name: 'offset',
                options: {
                    offset: [0, 4],
                },
            },
            { name: 'preventOverflow', enabled: preventOverflow },
        ],
    });

    // Callers pass a new object on each render.
    const lockScrollRef = useRef(lockScroll);
    useLayoutEffect(() => {
        lockScrollRef.current = lockScroll;
    });

    useEffect(() => {
        if (!popperElement) {
            return undefined;
        }

        const { scroll, wrapper } = lockScrollRef.current ?? {};
        scroll?.classList?.add('!overflow-hidden');
        wrapper?.classList?.add('!me-2');

        return () => {
            scroll?.classList?.remove('!overflow-hidden');
            wrapper?.classList?.remove('!me-2');
        };
    }, [popperElement]);

    // A menu in a list row does not take the row's Tab stop state.
    const menuContent = (
        <RowTabStopContext.Provider value={undefined}>
            {children}
        </RowTabStopContext.Provider>
    );

    return (
        <Menu>
            {({ close, open }) => {
                if (setActive && isOpen !== open) {
                    setIsOpen(open);
                    setActive(open);
                }

                return (
                    <>
                        <CloseOnTargetChange
                            targetKey={targetKey}
                            open={open}
                            close={close}
                            menuElement={popperElement}
                            triggerElement={referenceElement}
                        />
                        <MenuButton
                            ref={setReferenceElement}
                            aria-label={triggerLabel}
                            tabIndex={rowTabIndex}
                            className={twMerge(
                                'hover:darken-10 rounded-sm bg-gray-300 p-1',
                                className,
                            )}
                            onClick={(
                                event: React.MouseEvent<
                                    HTMLButtonElement,
                                    MouseEvent
                                >,
                            ) => {
                                event.stopPropagation();
                                setMenuButtonAnchor?.(event.currentTarget);

                                if (open) {
                                    event.preventDefault();
                                    close();
                                }
                            }}
                        >
                            <div className="flex" data-test-id={dataTestId}>
                                {icon || <Icon iconKey="more_vert" />}
                            </div>
                        </MenuButton>

                        {!skipPortal ? (
                            open &&
                            createPortal(
                                <div className="relative h-full">
                                    <MenuItems
                                        ref={setPopperElement}
                                        style={styles.popper}
                                        className={twMerge(
                                            'shadow-base end-0 z-50 my-1 inline-block w-max rounded-md bg-white p-3 dark:bg-gray-200',
                                            menuClassName,
                                        )}
                                    >
                                        {menuContent}
                                    </MenuItems>
                                </div>,
                                document.getElementById('contextMenuPortal'),
                            )
                        ) : (
                            // Rendered inside its trigger's parent, such as a
                            // chat message's action bar. The open menu takes
                            // focus, so it must not join a focusgroup around
                            // it, whose arrow keys would move focus out of it.
                            <MenuItems
                                ref={setPopperElement}
                                style={styles.popper}
                                focusgroup="none"
                                className={twMerge(
                                    'shadow-base end-0 z-50 my-1 inline-block w-max rounded-md bg-white p-3 dark:bg-gray-100',
                                    menuClassName,
                                )}
                            >
                                {menuContent}
                            </MenuItems>
                        )}
                    </>
                );
            }}
        </Menu>
    );
};

export default ContextMenu;
